import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {translate} from '../Translate';
import {TaskEditor} from '../task';

import backend from 'utils/backend';

import CommandResponseView from './CommandResponseView';
import SlashCommandTextbox from './SlashCommandTextbox';

import _ from 'lodash';

const Commands = (props) => {
  const {
    communicationTarget,
    requester,
    resources,
    statusProgressWrap: progressWrap,
    playbook,
    playbooks,
    skills,
  } = props;

  const requesterRn = requester.userRn;
  const [commandExecutionResponse, setCommandExecutionResponse] = React.useState(null);
  const [lastExecutedSkill, setLastExecutedSkill] = React.useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = React.useState(false);
  const [commandInputValue, setCommandInputValue] = React.useState('');

  const executeSkill = React.useCallback(async (skill, promptText) => {
    let response = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);

        const resourcesToSubmit = resources.map((resource) => {
          return _.pick(resource, ['type', 'rn']);
        });

        const requestBody = {
          ressources: [
            ...resourcesToSubmit,
            {type: 'USER', rn: requesterRn},
          ],
          text: promptText || ' ',
        };

        setLastExecutedSkill({
          promptText,
          skill,
        });

        response = await backend.skills.executeSkill(skill.skillRn, requestBody);
        setCommandExecutionResponse(response.result);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return response;
  }, [progressWrap, requesterRn, resources]);

  const handleRegenerate = React.useCallback(() => {
    if (!lastExecutedSkill) {
      return null;
    }

    return executeSkill(lastExecutedSkill.skill, lastExecutedSkill.promptText);
  }, [executeSkill, lastExecutedSkill]);

  const handleDiscard = React.useCallback(() => {
    setCommandExecutionResponse(null);
  }, []);

  const executePlaybook = React.useCallback(async (selectedPlaybook) => {
    const primaryResource = resources[0];

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        await playbook.execute(selectedPlaybook.playbookRn, primaryResource.rn);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });
  }, [playbook, progressWrap, resources]);

  const handleTaskSubmit = React.useCallback((taskData) => {
    progressWrap(async () => {
      try {
        await backend.task.addTask(taskData);
      } finally {
        setCommandInputValue('');
      }
    });
  }, [progressWrap]);

  const isTaskEditorMode = React.useMemo(() => {
    if (!commandInputValue) {
      return false;
    }

    return !commandInputValue.startsWith('/');
  }, [commandInputValue]);

  return (
    <Box>
      {
        commandExecutionResponse &&
        <CommandResponseView
          communicationTarget={communicationTarget}
          requester={requester}
          response={commandExecutionResponse}
          onDiscard={handleDiscard}
          isRegenerating={isExecutingCommand}
          onRegenerate={handleRegenerate}
        />
      }
      {
        isTaskEditorMode ?
          <Box
            sx={{
              my: '12px',
            }}
          >
            <TaskEditor
              autoFocusTitle
              onCancel={() => setCommandInputValue('')}
              onSubmit={handleTaskSubmit}
              task={{
                title: commandInputValue,
                connectionInfo: [
                  {
                    connectionRn: resources[0].rn,
                    firstName: resources[0].firstName,
                    lastName: resources[0].lastName || '',
                  },
                ],
              }}
            />
          </Box> :
          <SlashCommandTextbox
            isExecutingCommand={isExecutingCommand}
            onExecutePlaybook={executePlaybook}
            onExecuteSkill={executeSkill}
            placeholder={translate('playbooks.List.ExecutePlaceholder')}
            playbooks={playbooks}
            onInputValueChange={setCommandInputValue}
            skills={skills}
          />
      }
    </Box>
  );
};

Commands.propTypes = {
  communicationTarget: PropTypes.object,
  requester: PropTypes.object.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    rn: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  })).isRequired,
  playbooks: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  playbook: PropTypes.shape({
    execute: PropTypes.func,
  }),
};

const mapStateToProps = (state) => ({
  requester: state.user,
  playbooks: state.playbooks.allPlaybooks,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(Commands));
